import { useMemo, useState } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';

import { CloseCircleOutline } from '@aslanpay/portal-common-lib';

import useFetchIntegrationConfigSpec from 'app/hooks/useFetchIntegrationConfigSpec';

import { ConfigSpecFieldInput, applicableConfigFields } from '../IntegrationForm';

import { AddIntegrationTypeFormValues, AddIntegrationTypeModalProps } from './types';

/** Collects the config values a type needs (per the provider's spec) before it is enabled - the
 * counterpart to picking them at initial registration, for a type being added to an already-existing
 * connection. Renders nothing to fill in when the type has no applicable spec fields; the caller only
 * opens this when it does (see AddTypeMenu). */
function AddIntegrationTypeModal({
  integration,
  integrationType,
  isOpen,
  onClose,
  onConfirm,
}: AddIntegrationTypeModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: specFields = [] } = useFetchIntegrationConfigSpec(integration.provider);

  const fields = useMemo(
    () =>
      applicableConfigFields(specFields, {
        protocol: integration.protocol,
        integrationTypes: [integrationType],
      }),
    [specFields, integration.protocol, integrationType]
  );

  const methods = useForm<AddIntegrationTypeFormValues>({
    mode: 'onChange',
    values: {
      configValues: Object.fromEntries(
        fields.map((field) => [field.key, integration.config?.[field.key] ?? ''])
      ),
    },
  });
  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const onSubmit = async (data: AddIntegrationTypeFormValues) => {
    const populated = Object.fromEntries(
      Object.entries(data.configValues ?? {}).filter(([, value]) => value.trim().length > 0)
    );

    setIsSubmitting(true);
    try {
      await onConfirm(populated);
      onClose();
    } catch {
      // The caller already surfaces an error toast; keep the modal open so the user can retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent w={{ base: '100vw', sm: '90vw', md: '480px' }}>
        <ModalHeader>
          <HStack>
            <Text as="h6">Add integration type</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close add integration type"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <form id="add-integration-type-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <VStack spacing="16px" align="stretch">
                {fields.map((field) => (
                  <ConfigSpecFieldInput key={field.key} field={field} />
                ))}
              </VStack>
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-integration-type-form"
              size="sm"
              isDisabled={!isValid}
              isLoading={isSubmitting}
            >
              Add
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddIntegrationTypeModal;
